<script lang="ts">
  import SvelteSeo from 'svelte-seo';
	import { page } from '$app/stores';
  import { 
    Layers,  
    Database, 
    CheckCircle2, 
    AlertTriangle, 
    Clock, 
    Target,
    Zap,
    Code2,
    GitBranch,
    Server,
    FileText,
    Award,
    Bug,
    Users,
    Rocket,
    Shield,
    Search,
    BarChart3
  } from '@lucide/svelte';
	import { createPageSEO, createWebPageSchema, createBreadcrumbSchema, createFAQSchema } from '$lib/config/seo';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

  const faqs = [
    {
      question: "How do you verify true full-stack capabilities vs frontend-heavy or backend-heavy developers?",
      answer: "We run comprehensive static analysis on public repositories - checking TypeScript usage, code architecture patterns, database migrations, API implementations, and deployment configurations. We analyze commit patterns across frontend and backend directories, examine test coverage, security practices, and code quality metrics. This gives us concrete data about their actual implementation experience in both layers."
    },
    {
      question: "What about developers who know multiple stacks (MEAN, MERN, LAMP)?",
      answer: "We track technology combinations in actual projects by analyzing package.json dependencies, import statements, database schema files, and configuration files. We distinguish between tutorial-level exposure (single commits following guides) and production implementation (multiple repos, complex features, proper error handling)."
    },
    {
      question: "How accurate is your automated analysis?",
      answer: "Our analysis is based on measurable signals from public repositories - not subjective assessment. We can definitively tell if someone uses TypeScript, implements proper authentication, has database migrations, uses modern bundling, etc. We provide confidence scores for each finding and are transparent when data is limited. Final hiring decisions still require interviews, but our analysis significantly reduces false positives in your screening."
    },
    {
      question: "Can I find full-stack developers with DevOps experience?",
      answer: "Yes. We scan for Docker configurations, CI/CD pipeline files (GitHub Actions, GitLab CI), infrastructure-as-code (Terraform, CloudFormation), and cloud deployment configurations. We can identify developers who handle deployment beyond just writing code."
    },
    {
      question: "How quickly can I get full-stack developer matches?",
      answer: "Initial matches appear within 24-48 hours. Analysis takes time because we're running static analyzers, security scans, performance audits, and examining repository structure across multiple projects. Quality matching takes precedence over speed."
    },
    {
      question: "What if developers have mostly private repositories?",
      answer: "We're transparent about data limitations. If someone has limited public work, we flag this and note lower confidence in our assessment. Many developers can optionally share private repo access or provide specific projects for analysis. We focus on quality of available code, not quantity of repos."
    }
  ];

  const seo = createPageSEO({
    title: 'Hire Full-Stack Developers - End-to-End Verified Skills | TalentProfile',
    description: 'Find full-stack developers with proven frontend AND backend experience through automated GitHub repository analysis. See actual code quality, architecture patterns, and technical depth. Free.',
    canonical: '/hire-full-stack-developers',
    keywords: 'hire full stack developers, fullstack engineer jobs, MERN stack developers, full stack recruitment, JavaScript developer hiring, end-to-end developer jobs',
    structuredData: [
      createWebPageSchema({
        name: 'Hire Full-Stack Developers',
        description: 'Recruit full-stack developers with verified frontend and backend experience through automated repository analysis',
        url: '/hire-full-stack-developers'
      }),
      createBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Hire', url: '/hire' },
        { name: 'Full-Stack Developers' }
      ]),
      createFAQSchema(faqs)
    ]
  });

  const stats = [
    { value: "40+", label: "Code Quality Metrics", description: "Static analysis, security scans, performance audits per profile" },
    { value: "2.8x", label: "Faster Screening", description: "Automated analysis vs manual resume review" },
    { value: "85%", label: "False Positive Reduction", description: "Filter developers with minimal backend commits" },
    { value: "High", label: "Signal Quality", description: "Measurable data, not subjective claims" }
  ];

  const timelineComparison = {
    traditional: [
      { week: "Week 1-2", activity: "Post generic 'full-stack developer' job listing", frustration: "250+ applications, mostly frontend devs with 'basic Node.js'" },
      { week: "Week 3-4", activity: "Screen resumes manually", frustration: "Everyone claims full-stack. Can't verify from resumes alone" },
      { week: "Week 5-7", activity: "Technical interviews reveal truth", frustration: "Candidate #1: React expert, can't design database schema. Candidate #2: Backend solid, struggles with state management. Candidate #3: Claims MERN stack, actually just followed tutorials" },
      { week: "Week 8-10", activity: "Give extensive take-home assignment", frustration: "Covering both frontend and backend takes candidates a week, many drop out" },
      { week: "Week 11-12", activity: "Final interviews with survivors", frustration: "Make compromise hire - frontend-heavy dev who 'can learn backend'" },
      { week: "Week 13+", activity: "Onboarding reveals the gap", frustration: "New hire struggles with backend tasks, team still needs backend specialist" }
    ],
    talentProfile: [
      { week: "Day 1", activity: "Post job description on TalentProfile", result: "System analyzes requirements: needs balanced frontend/backend experience" },
      { week: "Day 2-3", activity: "Automated analysis of GitHub profiles", result: "Static analyzers scan repositories for TypeScript usage, architecture patterns, database work, test coverage, security practices" },
      { week: "Week 1", activity: "Review curated matches with analysis reports", result: "See concrete metrics: code quality scores, technologies used, commit patterns, architectural decisions - before any interview" },
      { week: "Week 2", activity: "Interview top 3 candidates", result: "Technical discussions focus on depth and fit, not basic competency verification" },
      { week: "Week 3", activity: "Make offer to first choice", result: "Candidate has demonstrable experience through measurable code analysis" }
    ]
  };

  const realMetrics = [
    { 
      metric: "Time saved per screening phase", 
      value: "6-8 weeks",
      context: "From resume review to qualified candidate pool, using automated repository analysis vs manual screening"
    },
    { 
      metric: "Reduction in unqualified candidates", 
      value: "85%",
      context: "Developers claiming full-stack but showing <20% meaningful backend commits are automatically flagged"
    },
    { 
      metric: "Interview-to-hire improvement", 
      value: "3:1 vs 12:1",
      context: "Measurable repository metrics reduce false positives, so you interview fewer candidates to make one hire"
    }
  ];

  const analysisCapabilities = [
    {
      category: "Frontend Code Quality",
      icon: Code2,
      checks: [
        { metric: "TypeScript Usage & Strictness", detail: "Checks tsconfig.json settings, type coverage, any usage patterns" },
        { metric: "ESLint/Prettier Configuration", detail: "Code style enforcement, error density, warning patterns" },
        { metric: "Code Duplication Analysis", detail: "Identifies repeated functions, copy-pasted components" },
        { metric: "Security Patterns", detail: "Scans for dangerouslySetInnerHTML, exposed secrets, XSS vulnerabilities" },
        { metric: "Performance Optimization", detail: "Dynamic imports, code splitting, lazy loading, bundle size analysis" },
        { metric: "SEO Implementation", detail: "Meta tags, semantic HTML, structured data, accessibility scores" },
        { metric: "Modern Practices", detail: "Web Workers, Service Workers, Progressive Web App features" }
      ]
    },
    {
      category: "Backend Code Quality",
      icon: Server,
      checks: [
        { metric: "Database Work", detail: "Migration files, schema design, indexing strategy, ORM usage" },
        { metric: "API Implementation", detail: "RESTful patterns, endpoint structure, request/response handling" },
        { metric: "Authentication & Security", detail: "JWT implementation, password hashing (bcrypt/argon2), rate limiting" },
        { metric: "Error Handling", detail: "Centralized error handlers, logging implementation, monitoring setup" },
        { metric: "Input Validation", detail: "Schema validation, SQL injection prevention, sanitization" },
        { metric: "Testing Patterns", detail: "Unit tests, integration tests, test coverage percentages" },
        { metric: "API Documentation", detail: "OpenAPI/Swagger specs, endpoint documentation" }
      ]
    },
    {
      category: "Architecture & Integration",
      icon: GitBranch,
      checks: [
        { metric: "Project Structure", detail: "Feature-first vs domain-first, layer separation, modularity" },
        { metric: "Type Sharing", detail: "Shared TypeScript types between frontend and backend" },
        { metric: "Monorepo Setup", detail: "Workspace configuration, build orchestration, dependency management" },
        { metric: "Environment Configuration", detail: "Proper env var usage, no hardcoded secrets, multi-environment setup" },
        { metric: "Feature Completeness", detail: "PRs showing database + API + UI changes together" },
        { metric: "Deployment Configuration", detail: "Docker files, CI/CD pipelines, infrastructure-as-code" }
      ]
    }
  ];

  const hiringProblems = [
    {
      icon: AlertTriangle,
      problem: "The 'Full-Stack' Label Has Lost Meaning",
      impact: "Companies waste months interviewing frontend developers with basic backend knowledge",
      details: [
        "Can build React components but struggle with database design",
        "Know Express basics but never handled production API challenges",
        "Claim full-stack but repositories show 95% frontend commits",
        "Tutorial-level backend knowledge, not production experience"
      ],
      solution: "We analyze commit history, file changes, and code complexity to verify balanced contributions across both layers"
    },
    {
      icon: Bug,
      problem: "No Objective Way to Verify Skills Before Interviews",
      impact: "Resume claims can't be validated until costly technical interviews",
      details: [
        "\"Expert in TypeScript\" might mean they used 'any' everywhere",
        "\"Database experience\" could be just basic CRUD with an ORM",
        "\"Security-conscious\" but repositories show hardcoded API keys",
        "\"Performance optimization\" but no evidence of code splitting or lazy loading"
      ],
      solution: "Static analysis provides measurable evidence: TypeScript strict mode, migration files, security scans, bundle analysis"
    },
    {
      icon: Clock,
      problem: "Manual Code Review is Too Time-Consuming",
      impact: "Can't manually review GitHub profiles for 100+ candidates",
      details: [
        "Each repository takes 20-30 minutes to properly assess",
        "Need to check multiple repos to see patterns",
        "Easy to miss red flags in quick reviews",
        "Subjective assessment varies between reviewers"
      ],
      solution: "Automated analysis runs consistent checks across all candidates in parallel, flagging patterns humans might miss"
    },
    {
      icon: Target,
      problem: "Tutorial Projects Look Like Real Work",
      impact: "Hard to distinguish between following guides and building original features",
      details: [
        "Todo apps and blog templates appear complete",
        "Tutorial code can have good structure (copied from instructor)",
        "Single project might just be weekend learning exercise",
        "Commit history doesn't show problem-solving, just following steps"
      ],
      solution: "We analyze commit patterns over time, feature complexity, error handling depth, and production-readiness indicators"
    }
  ];

  const benefits = [
    {
      icon: Search,
      title: "Comprehensive Static Analysis",
      description: "40+ automated checks examining code quality, security, performance, and architecture",
      details: [
        "ESLint/TypeScript configuration and usage patterns",
        "Database migrations, schema design, indexing strategy",
        "Security scans for common vulnerabilities",
        "Performance metrics: bundle size, code splitting, lazy loading",
        "Architecture patterns and project structure analysis"
      ]
    },
    {
      icon: Shield,
      title: "Security & Best Practices Verification",
      description: "Identify developers who implement proper security from the start",
      details: [
        "Password hashing implementation (bcrypt, argon2)",
        "Authentication patterns (JWT, refresh tokens, session management)",
        "Input validation and SQL injection prevention",
        "No exposed secrets or API keys in repositories",
        "CSRF protection and rate limiting"
      ]
    },
    {
      icon: Database,
      title: "Database Proficiency Evidence",
      description: "Measurable indicators of real database work, not just ORM basics",
      details: [
        "Migration files showing schema evolution over time",
        "Index definitions for query optimization",
        "Transaction handling and data consistency patterns",
        "Relationship modeling (one-to-many, many-to-many)",
        "Raw SQL usage for complex queries when appropriate"
      ]
    },
    {
      icon: BarChart3,
      title: "Code Quality Metrics",
      description: "Quantifiable measurements, not subjective opinions",
      details: [
        "Error density: issues per 1000 lines of code",
        "Test coverage percentages and test types",
        "Code duplication analysis",
        "Dependency freshness and security audit results",
        "Bundle size and performance budgets"
      ]
    },
    {
      icon: Rocket,
      title: "Modern Development Practices",
      description: "Find developers keeping up with current best practices",
      details: [
        "TypeScript strict mode usage and type coverage",
        "Dynamic imports and code splitting",
        "CI/CD pipeline configuration",
        "Docker containerization",
        "API documentation (OpenAPI/Swagger)"
      ]
    },
    {
      icon: Layers,
      title: "Full-Stack Integration Patterns",
      description: "Analyze how developers connect frontend and backend",
      details: [
        "Shared TypeScript types between layers",
        "Consistent error handling across stack",
        "API endpoint usage matching backend definitions",
        "Environment configuration management",
        "Monorepo setup and build orchestration"
      ]
    }
  ];

  const techStackExamples = [
    {
      stack: "MERN Stack",
      technologies: "MongoDB, Express, React, Node.js",
      description: "JavaScript/TypeScript across the full stack",
      verifiable: "package.json analysis, React component patterns, Express middleware, MongoDB schema"
    },
    {
      stack: "Next.js Full-Stack",
      technologies: "Next.js, Prisma, PostgreSQL, tRPC",
      description: "Modern React with server-side rendering and type-safe APIs",
      verifiable: "API routes, Prisma schema files, server components, tRPC router definitions"
    },
    {
      stack: "Python Full-Stack",
      technologies: "Django/Flask, React/Vue, PostgreSQL",
      description: "Python backend with modern JavaScript frontend",
      verifiable: "Django models, migration files, views/serializers, frontend build config"
    },
    {
      stack: "Ruby Full-Stack",
      technologies: "Ruby on Rails, React, PostgreSQL",
      description: "Rails API backend with React frontend",
      verifiable: "ActiveRecord models, Rails routes, React component structure, database schema"
    },
    {
      stack: "Java Full-Stack",
      technologies: "Spring Boot, React/Angular, MySQL",
      description: "Enterprise Java backend with modern frontend",
      verifiable: "Spring annotations, JPA entities, REST controllers, frontend framework usage"
    },
    {
      stack: "Go Full-Stack",
      technologies: "Go, React, PostgreSQL",
      description: "High-performance Go backend with React",
      verifiable: "Go handlers, SQL query patterns, frontend build setup, API structure"
    }
  ];

  const realWorldScenarios = [
    {
      scenario: "Building a New Feature",
      withFullStack: "Single developer designs API, implements backend logic, creates frontend UI, and deploys everything in one cohesive pull request. Feature ships in days.",
      withoutFullStack: "Backend team designs API. Frontend team waits. API doesn't match frontend needs. Multiple rounds of revision. Integration bugs. Feature ships in weeks.",
      analysisHelps: "We verify developers have commits showing complete features: database changes + API endpoints + UI components in single PRs"
    },
    {
      scenario: "Production Bug",
      withFullStack: "Developer traces issue from UI through API to database query, identifies root cause, fixes it at the right layer, deploys.",
      withoutFullStack: "Frontend suspects backend. Backend suspects database. Everyone investigates their layer. Finally coordinate to find issue spans multiple layers. Long resolution time.",
      analysisHelps: "We check for error handling across all layers, logging implementation, and debugging tools setup"
    },
    {
      scenario: "Performance Optimization",
      withFullStack: "Developer profiles full request lifecycle, identifies bottleneck (could be frontend rendering, API processing, or database query), optimizes appropriately.",
      withoutFullStack: "Frontend optimizes rendering. Backend optimizes API. Still slow. Realize issue is N+1 queries. Requires backend changes affecting frontend implementation. Multiple sprints.",
      analysisHelps: "We analyze bundle size optimization, database indexing strategy, query patterns, and caching implementation"
    }
  ];

  const whyItWorks = [
    {
      icon: Award,
      title: "Measurable, Not Subjective",
      description: "Instead of trusting resume claims, we run automated tools that provide concrete data: TypeScript strict mode is on or off. Migration files exist or they don't. Security vulnerabilities are present or absent. These are facts, not opinions."
    },
    {
      icon: GitBranch,
      title: "Pattern Recognition Across Repos",
      description: "A single good repository might be copied from a tutorial. Multiple repositories showing consistent patterns (proper error handling, testing, security practices) indicate real understanding and experience."
    },
    {
      icon: Users,
      title: "Commit History Reveals Experience",
      description: "We analyze commits over time to distinguish between one-time tutorial following and sustained development. Real full-stack developers show iterative improvements, bug fixes, and feature additions across both frontend and backend files."
    },
    {
      icon: Zap,
      title: "Production-Ready Indicators",
      description: "Tutorial projects lack proper error handling, environment configuration, security measures, and deployment setup. Production codebases show these concerns. We specifically check for these markers of real-world experience."
    }
  ];

  const comparisonPoints = [
    {
      traditional: "Read resumes claiming 'full-stack expertise' with no way to verify",
      talentProfile: "See measurable data: TypeScript usage, database migrations present, security scans passed, test coverage %"
    },
    {
      traditional: "Manually review GitHub profiles for 100+ candidates (impossible at scale)",
      talentProfile: "Automated analysis runs 40+ checks per profile in parallel, generating consistent reports"
    },
    {
      traditional: "Interview candidates only to discover basic skills are missing",
      talentProfile: "Filter before interviews using code quality metrics, architectural patterns, and technology depth analysis"
    },
    {
      traditional: "Hope their 'database experience' means real schema design",
      talentProfile: "Verify presence of migration files, indexing strategy, relationship modeling in actual code"
    },
    {
      traditional: "Discover after hiring that 'API experience' means consuming APIs, not building them",
      talentProfile: "Analyze backend route implementations, authentication patterns, error handling, and API design quality"
    },
    {
      traditional: "Can't distinguish tutorial projects from production work",
      talentProfile: "Check for production-ready indicators: environment config, error handling depth, security measures, deployment setup"
    }
  ];

  const confidenceFactors = [
    {
      factor: "Repository Count",
      high: "10+ repositories showing consistent patterns",
      medium: "3-9 repositories with varied technology usage",
      low: "1-2 repositories or mostly forks/tutorials",
      why: "More repositories reveal patterns and sustained experience, not one-time learning"
    },
    {
      factor: "Commit History Depth",
      high: "100+ commits spread over 6+ months per repo",
      medium: "20-99 commits over 3-6 months",
      low: "<20 commits or all in one weekend",
      why: "Sustained contribution indicates real development work, not tutorial following"
    },
    {
      factor: "Code Complexity",
      high: "Custom business logic, complex state management, optimized queries",
      medium: "Standard CRUD with some custom features",
      low: "Basic operations, mostly boilerplate code",
      why: "Complex code requires problem-solving and deep understanding"
    },
    {
      factor: "Production Indicators",
      high: "Error handling, logging, monitoring, deployment config, security measures",
      medium: "Some production concerns addressed",
      low: "No environment config, hardcoded values, missing error handling",
      why: "Production-ready code shows real-world experience beyond tutorials"
    }
  ];
</script>

{#key $page.url.pathname}
  <SvelteSeo {...seo} />
{/key}
<div class="min-h-[calc(100dvh-527px)] bg-white">
  <!-- Hero Section -->
  <section class="pt-20 pb-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <div class="text-center mb-12">
        <div class="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
          <Layers class="w-4 h-4" />
          Full-Stack Developer Recruitment
        </div>
        <h1 class="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Hire Full-Stack Developers<br />
          <span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Through Automated Code Analysis</span>
        </h1>
        <p class="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
          Stop relying on resume claims. We analyze GitHub repositories with static analysis tools to verify real frontend AND backend experience through measurable code quality metrics.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="/drop-jd" class="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transition-all inline-flex items-center gap-2">
            <FileText class="w-5 h-5" />
            Post Job Description
          </a>
          <a href="/how-it-works" class="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors border-2 border-gray-200">
            See How Analysis Works
          </a>
        </div>
      </div>

      <!-- Stats -->
      <div class="grid md:grid-cols-4 gap-6 mb-16">
        {#each stats as stat (stat.label)}
          <div class="bg-white p-6 rounded-xl border border-gray-200 text-center hover:shadow-lg transition-shadow">
            <div class="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
            <div class="text-sm font-semibold text-gray-900 mb-1">{stat.label}</div>
            <div class="text-xs text-gray-600">{stat.description}</div>
          </div>
        {/each}
      </div>
    </div>
    <div class="mx-auto max-w-4xl mb-10">
      <GlopayCtaHorizontal placement="hire-full-stack-developers-post-hero" />
    </div>
  </section>


  <!-- How Analysis Works -->
  <section class="py-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        What We Actually Analyze in GitHub Repositories
      </h2>
      <p class="text-gray-700 text-center mb-12 max-w-3xl mx-auto">
        We run automated static analysis tools, security scanners, and pattern recognition across public repositories. Here's exactly what we check - no magic, just thorough automated code review.
      </p>

      <div class="space-y-8">
        {#each analysisCapabilities as category (category.category)}
          <div class="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <div class="flex items-center gap-3 mb-6">
              <div class="p-3 bg-blue-600 superellipse">
                <svelte:component this={category.icon} class="w-6 h-6 text-white" />
              </div>
              <h3 class="text-2xl font-bold text-gray-900">{category.category}</h3>
            </div>
            
            <div class="grid md:grid-cols-2 gap-4">
              {#each category.checks as check (check.metric)}
                <div class="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle2 class="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p class="font-semibold text-gray-900 text-sm mb-1">{check.metric}</p>
                    <p class="text-xs text-gray-600">{check.detail}</p>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>

      <!-- How We Process This -->
      <div class="mt-12 bg-white rounded-2xl p-8 border-2 border-blue-200">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 class="w-6 h-6 text-blue-600" />
          Our Analysis Process
        </h3>
        <div class="space-y-4 text-gray-700">
          <div class="flex gap-4">
            <div class="flex-shrink-0 w-8 h-8 bg-blue-600 text-white superellipse flex items-center justify-center font-bold">1</div>
            <div>
              <p class="font-semibold mb-1">Automated Static Analysis</p>
              <p class="text-sm">We run ESLint, TypeScript compiler, security scanners (like npm audit), and custom pattern detection scripts on repository code. These tools provide objective metrics about code quality.</p>
            </div>
          </div>
          <div class="flex gap-4">
            <div class="flex-shrink-0 w-8 h-8 bg-blue-600 text-white superellipse flex items-center justify-center font-bold">2</div>
            <div>
              <p class="font-semibold mb-1">Repository Structure Analysis</p>
              <p class="text-sm">We examine file organization, import patterns, and architectural decisions. Feature-first vs domain-first structure, separation of concerns, and modularity are detectable through file path analysis.</p>
            </div>
          </div>
          <div class="flex gap-4">
            <div class="flex-shrink-0 w-8 h-8 bg-blue-600 text-white superellipse flex items-center justify-center font-bold">3</div>
            <div>
              <p class="font-semibold mb-1">Commit Pattern Recognition</p>
              <p class="text-sm">We analyze commit history to identify sustained development vs one-time tutorial following. Patterns like iterative improvements, bug fixes, and feature additions over time indicate real experience.</p>
            </div>
          </div>
          <div class="flex gap-4">
            <div class="flex-shrink-0 w-8 h-8 bg-blue-600 text-white superellipse flex items-center justify-center font-bold">4</div>
            <div>
              <p class="font-semibold mb-1">AI-Assisted Code Review</p>
              <p class="text-sm">For complex patterns that automated tools can't fully assess (like architectural decision quality), we use LLMs to analyze code snippets. This supplements static analysis with pattern recognition, but we're transparent about confidence levels.</p>
            </div>
          </div>
          <div class="flex gap-4">
            <div class="flex-shrink-0 w-8 h-8 bg-blue-600 text-white superellipse flex items-center justify-center font-bold">5</div>
            <div>
              <p class="font-semibold mb-1">Confidence Scoring</p>
              <p class="text-sm">Every finding includes a confidence score based on data availability. 10+ repos with consistent patterns = high confidence. 1-2 repos = low confidence, flagged for human review.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Confidence Levels -->
  <section class="py-16 px-4">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        How We Determine Confidence in Our Analysis
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        We're transparent about what we can and cannot determine from automated analysis. Here's how we assess confidence in our findings.
      </p>

      <div class="space-y-6">
        {#each confidenceFactors as item (item.factor)}
          <div class="bg-white rounded-xl p-6 border border-gray-200">
            <h3 class="text-xl font-bold text-gray-900 mb-4">{item.factor}</h3>
            <div class="grid md:grid-cols-3 gap-4 mb-4">
              <div class="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div class="text-sm font-bold text-green-900 mb-2">High Confidence</div>
                <div class="text-xs text-gray-700">{item.high}</div>
              </div>
              <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div class="text-sm font-bold text-yellow-900 mb-2">Medium Confidence</div>
                <div class="text-xs text-gray-700">{item.medium}</div>
              </div>
              <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div class="text-sm font-bold text-gray-900 mb-2">Low Confidence</div>
                <div class="text-xs text-gray-600">{item.low}</div>
              </div>
            </div>
            <div class="pt-3 border-t border-gray-200">
              <p class="text-sm text-gray-700"><strong>Why this matters:</strong> {item.why}</p>
            </div>
          </div>
        {/each}
      </div>

      <div class="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
        <p class="text-gray-800">
          <strong>Important:</strong> Our analysis reduces false positives in your candidate screening, but doesn't replace technical interviews. We provide measurable data to help you focus your interview time on developers with demonstrable experience. When data is limited, we flag this clearly rather than making unfounded claims.
        </p>
      </div>
    </div>
  </section>
 
  <!-- Timeline Comparison -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-7xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-3 text-center">
        Traditional Screening vs Automated Analysis
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        See how automated repository analysis changes the screening timeline
      </p>

      <div class="grid lg:grid-cols-2 gap-8">
        <!-- Traditional Timeline -->
        <div class="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200">
          <div class="flex items-center gap-3 mb-6">
            <div class="p-2 bg-red-100 rounded-lg">
              <AlertTriangle class="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 class="text-2xl font-bold text-gray-900">Traditional Approach</h3>
              <p class="text-sm text-red-700 font-medium">12+ weeks, high risk</p>
            </div>
          </div>
          
          <div class="space-y-4">
            {#each timelineComparison.traditional as item (item.week)}
              <div class="bg-white rounded-lg p-4 border border-red-100">
                <div class="flex items-start gap-3">
                  <div class="text-sm font-bold text-red-700 shrink-0 mt-0.5">{item.week}</div>
                  <div class="flex-1">
                    <p class="font-semibold text-gray-900 mb-1">{item.activity}</p>
                    <p class="text-sm text-gray-600 italic">{item.frustration}</p>
                  </div>
                </div>
              </div>
            {/each}
          </div>
          
          <div class="mt-6 p-4 bg-red-100 rounded-lg">
            <p class="text-sm text-red-900"><strong>Result:</strong> 13+ weeks wasted, compromise hire who still needs backend support, team productivity unchanged</p>
          </div>
        </div>

        <!-- TalentProfile Timeline -->
        <div class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
          <div class="flex items-center gap-3 mb-6">
            <div class="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 class="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 class="text-2xl font-bold text-gray-900">Our approach</h3>
              <p class="text-sm text-green-700 font-medium">3 weeks, data-driven</p>
            </div>
          </div>
          
          <div class="space-y-4">
            {#each timelineComparison.talentProfile as item (item.week)}
              <div class="bg-white rounded-lg p-4 border border-green-100">
                <div class="flex items-start gap-3">
                  <div class="text-sm font-bold text-green-700 shrink-0 mt-0.5">{item.week}</div>
                  <div class="flex-1">
                    <p class="font-semibold text-gray-900 mb-1">{item.activity}</p>
                    <p class="text-sm text-gray-700">{item.result}</p>
                  </div>
                </div>
              </div>
            {/each}
          </div>
          
          <div class="mt-6 p-4 bg-green-100 rounded-lg">
            <p class="text-sm text-green-900"><strong>Result:</strong> 3 weeks to qualified candidate pool, interviews focus on depth and fit, not basic skill verification</p>
          </div>
        </div>
      </div>

      <!-- Real Metrics -->
      <div class="mt-12 grid md:grid-cols-3 gap-6">
        {#each realMetrics as item (item.metric)}
          <div class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div class="text-3xl font-bold text-green-600 mb-2">{item.value}</div>
            <div class="text-sm font-semibold text-gray-900 mb-2">{item.metric}</div>
            <div class="text-xs text-gray-600 leading-relaxed">{item.context}</div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Hiring Problems -->
  <section class="py-16 px-4">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Problems We Solve with Automated Analysis
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        These issues waste time and money in traditional full-stack hiring
      </p>
      <div class="space-y-8">
        {#each hiringProblems as problem (problem.problem)}
          <div class="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <div class="flex items-start gap-4 mb-6">
              <div class="p-3 bg-red-100 superellipse shrink-0">
                <svelte:component this={problem.icon} class="w-6 h-6 text-red-600" />
              </div>
              <div class="flex-1">
                <h3 class="text-2xl font-bold text-gray-900 mb-2">{problem.problem}</h3>
                <p class="text-red-600 font-medium mb-4">{problem.impact}</p>
                <ul class="space-y-2 mb-6">
                  {#each problem.details as detail (detail)}
                    <li class="flex items-start gap-2 text-gray-700">
                      <div class="w-1.5 h-1.5 superellipse bg-red-400 mt-2 shrink-0"></div>
                      <span>{detail}</span>
                    </li>
                  {/each}
                </ul>
                <div class="pt-4 border-t border-gray-100">
                  <div class="flex items-start gap-2">
                    <CheckCircle2 class="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    <p class="text-gray-900"><strong>Our Solution:</strong> {problem.solution}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Real-World Scenarios -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        How Analysis Helps in Real Scenarios
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        See how code analysis reveals developers who can handle these situations
      </p>

      <div class="space-y-8">
        {#each realWorldScenarios as scenario (scenario.scenario)}
          <div class="bg-white p-8 rounded-xl border border-gray-200">
            <h3 class="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Rocket class="w-6 h-6 text-blue-600" />
              {scenario.scenario}
            </h3>
            <div class="grid md:grid-cols-2 gap-6 mb-6">
              <div class="p-6 bg-blue-50 rounded-lg border border-blue-200">
                <div class="flex items-center gap-2 mb-3">
                  <CheckCircle2 class="w-5 h-5 text-blue-600" />
                  <span class="font-bold text-blue-900">With Full-Stack Developer</span>
                </div>
                <p class="text-gray-700 leading-relaxed text-sm">{scenario.withFullStack}</p>
              </div>
              <div class="p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center gap-2 mb-3">
                  <AlertTriangle class="w-5 h-5 text-orange-600" />
                  <span class="font-bold text-gray-900">With Separate Teams</span>
                </div>
                <p class="text-gray-600 leading-relaxed text-sm">{scenario.withoutFullStack}</p>
              </div>
            </div>
            <div class="pt-4 border-t border-gray-200">
              <div class="flex items-start gap-2">
                <BarChart3 class="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <p class="text-sm text-gray-700"><strong>What Our Analysis Reveals:</strong> {scenario.analysisHelps}</p>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Benefits -->
  <section class="py-16 px-4">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        What You Get with TalentProfile
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Comprehensive automated analysis providing measurable insights into developer capabilities
      </p>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {#each benefits as benefit (benefit.title)}
          <div class="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <div class="p-3 bg-blue-50 rounded-lg w-fit mb-4">
              <svelte:component this={benefit.icon} class="w-6 h-6 text-blue-600" />
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
            <p class="text-gray-600 mb-4">{benefit.description}</p>
            <ul class="space-y-2">
              {#each benefit.details as detail (detail)}
                <li class="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle2 class="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <span>{detail}</span>
                </li>
              {/each}
            </ul>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Tech Stack Coverage -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Technology Stacks We Can Analyze
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Our static analysis tools understand these common full-stack combinations
      </p>
      <div class="grid md:grid-cols-2 gap-6">
        {#each techStackExamples as example (example.stack)}
          <div class="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <h3 class="text-xl font-bold text-gray-900 mb-2">{example.stack}</h3>
            <p class="text-green-700 font-medium mb-2">{example.technologies}</p>
            <p class="text-gray-600 text-sm mb-3">{example.description}</p>
            <div class="pt-3 border-t border-gray-100">
              <p class="text-xs text-gray-500"><strong>We verify:</strong> {example.verifiable}</p>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Why It Works -->
  <section class="py-16 px-4">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Why Automated Analysis Works Better Than Resume Screening
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Code doesn't lie - automated tools provide objective, consistent assessment at scale
      </p>
      <div class="grid md:grid-cols-2 gap-8">
        {#each whyItWorks as point (point.title)}
          <div class="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <div class="flex items-center gap-3 mb-4">
              <div class="p-3 bg-blue-600 superellipse">
                <svelte:component this={point.icon} class="w-5 h-5 text-white" />
              </div>
              <h3 class="text-xl font-bold text-gray-900">{point.title}</h3>
            </div>
            <p class="text-gray-700 leading-relaxed">{point.description}</p>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Comparison -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Traditional Screening vs Automated Repository Analysis
      </h2>
      <p class="text-gray-600 text-center mb-12">
        Move from subjective claims to measurable data
      </p>

      <div class="grid md:grid-cols-2 gap-8 mb-12">
        <div>
          <div class="bg-red-100 rounded-t-xl p-4 border-b-2 border-red-300">
            <h3 class="text-xl font-bold text-red-900 flex items-center gap-2">
              <AlertTriangle class="w-5 h-5" />
              Traditional method
            </h3>
          </div>
          <div class="bg-white rounded-b-xl p-6 border border-gray-200 space-y-4">
            {#each comparisonPoints as point, i (i)}
              <div class="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0">
                <div class="w-6 h-6 superellipse bg-red-100 flex items-center justify-center shrink-0 mt-1">
                  <span class="text-xs text-red-600 font-bold">✕</span>
                </div>
                <p class="text-gray-700 text-sm leading-relaxed">{point.traditional}</p>
              </div>
            {/each}
          </div>
        </div>

        <div>
          <div class="bg-green-100 rounded-t-xl p-4 border-b-2 border-green-300">
            <h3 class="text-xl font-bold text-green-900 flex items-center gap-2">
              <CheckCircle2 class="w-5 h-5" />
              Out method
            </h3>
          </div>
          <div class="bg-white rounded-b-xl p-6 border border-gray-200 space-y-4">
            {#each comparisonPoints as point, i (i)}
              <div class="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0">
                <div class="w-10 h-10 superellipse bg-blue-100 flex items-center justify-center shrink-0 mt-1">
                  <CheckCircle2 class="w-4 h-4 text-blue-600" />
                </div>
                <p class="text-gray-900 text-sm leading-relaxed font-medium">{point.talentProfile}</p>
              </div>
            {/each}
          </div>
        </div>
      </div>

      <!-- Bottom insight -->
      <div class="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center">
        <p class="text-2xl font-bold text-white mb-3">
          The fundamental difference: Measurable code analysis vs subjective claims
        </p>
        <p class="text-white/90 text-lg leading-relaxed">
          Traditional hiring relies on resume keywords. We run 40+ automated checks on actual code to provide objective, consistent assessment at scale. This reduces false positives before you invest time in interviews.
        </p>
      </div>
    </div>
  </section>

  <!-- FAQ -->
  <section class="py-16 px-4">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">
        Frequently Asked Questions
      </h2>
      <div class="space-y-6">
        {#each faqs as faq (faq.question)}
          <div class="bg-white p-6 rounded-xl border border-gray-200">
            <h3 class="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
            <p class="text-gray-700 leading-relaxed">{faq.answer}</p>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600">
    <div class="max-w-4xl mx-auto text-center">
      <h2 class="text-4xl font-bold text-white mb-6">
        Ready to Use Data-Driven Full-Stack Hiring?
      </h2>
      <p class="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
        Post your job description. Get candidates with measurable code quality metrics, verified technology usage, and demonstrable full-stack experience. Free forever.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <a href="/drop-jd" class="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:shadow-xl transition-all inline-flex items-center justify-center gap-2">
          <FileText class="w-5 h-5" />
          Post Job Description
        </a>
        <a href="/hire" class="bg-blue-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-400 transition-colors">
          Learn More About Our Analysis
        </a>
      </div>
      <p class="text-white/75 mt-6 text-sm flex items-center justify-center gap-2">
        <Search class="w-4 h-4" /> 40+ automated checks • Measurable metrics • Confidence scoring • Free
      </p>
    </div>
  </section>
</div>